import { useState } from 'react'
import { AlertCircle, Send } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { ReplyAttachments } from '@/components/reply-attachments'
import type { ReplyAttachmentDraft } from '@/features/communications/types'
import { answerErrorMessage, useAnswerQuoteComment } from './api'
import { ComposeTextarea } from '@/components/compose-field'

// What a comment takes (app/services/storefront_chat/attachments.py, shared with quote comments).
const FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt'
const MAX_FILES = 5
const MAX_FILE_BYTES = 10 * 1024 * 1024

/** Sales's answer on one item: text, files or both. Enter sends, Shift+Enter starts a new line. */
export function AnswerComposer({
  quoteId,
  rowRef,
  customerName,
  itemName,
}: {
  quoteId: string
  rowRef: string | null
  customerName: string | null
  itemName: string
}) {
  const answer = useAnswerQuoteComment(quoteId)
  const [text, setText] = useState('')
  const [files, setFiles] = useState<ReplyAttachmentDraft[]>([])
  const [attaching, setAttaching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canSend = (text.trim().length > 0 || files.length > 0) && !attaching
  const send = () => {
    if (!canSend || answer.isPending) return
    setError(null)
    answer.mutate(
      { rowRef, body: text.trim(), files },
      {
        onSuccess: () => {
          setText('')
          setFiles([])
        },
        onError: (err) => setError(answerErrorMessage(err)),
      },
    )
  }
  return (
    <div className="border-t bg-muted/20 px-3 py-3 sm:px-4">
      {error ? (
        <p
          className="mb-2 flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2 text-sm text-foreground"
          role="alert"
        >
          <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          {error}
        </p>
      ) : null}
      <div className="flex items-end gap-2">
        <ComposeTextarea
          aria-label={`Reply about ${itemName}`}
          className="max-h-48 min-h-[2.75rem] flex-1 resize-none bg-background text-sm"
          disabled={answer.isPending}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault()
              send()
            }
          }}
          placeholder={customerName ? `Reply to ${customerName}` : 'Reply'}
          rows={2}
          value={text}
        />
        <LoadingButton
          className="shrink-0"
          disabled={!canSend}
          loading={answer.isPending}
          loadingText="Sending..."
          onClick={send}
        >
          <Send className="h-3.5 w-3.5" />
          Send
        </LoadingButton>
      </div>
      <div className="mt-2">
        <ReplyAttachments
          accept={FILE_ACCEPT}
          attachments={files}
          attaching={attaching}
          disabled={answer.isPending}
          maxFileBytes={MAX_FILE_BYTES}
          maxFiles={MAX_FILES}
          maxTotalBytes={MAX_FILES * MAX_FILE_BYTES}
          onAttachingChange={setAttaching}
          onChange={setFiles}
        />
      </div>
    </div>
  )
}
